import {
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import { GrantCardComponent } from '../grant-card/grant-card.component';
import { GrantsService } from './grants.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { filtersOptions, sortOptions } from './filter-sort-config';
import { FormsModule } from '@angular/forms';
import { MitiDialogComponent } from './miti-dialog/miti-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-grants-list',
    standalone: true,
    imports: [
        GrantCardComponent,
        CommonModule,
        TranslateModule,
        MatIcon,
        FormsModule,
    ],
    templateUrl: './grants-list.component.html',
    styleUrl: './grants-list.component.scss',
})
export class GrantsListComponent implements OnInit {
    @Input() selectedLanguage: string = 'en';
    mitiData = [];
    showMainFilter = false;
    showSubFilters: { [key: number]: boolean } = {};
    selectedFilters: { [key: string]: string[] } = {};
    filteredMitiData: any[] = [];
    sort: boolean = false;
    @ViewChild('sortMenu', { static: false }) sortMenu!: ElementRef;
    @ViewChild('filterMenu', { static: false }) filterMenu!: ElementRef;
    @ViewChild('appliedFiltersDiv', { static: false })
    appliedFiltersDiv!: ElementRef;
    activeSort: string = '';
    activeFilters: boolean = false;
    sortOptions = sortOptions;
    appliedFilters: { name: string; value: string }[] = [];
    searchText: string = '';
    showAll: boolean = false;
    showAppliedFilter: boolean = false;
    imgs: string[] | null = null;
    totalMatchedCount: number = 0;
    isFiltersApplied = false;
    isFormFilter: boolean = false;
    formResults: any = null;
    private mitiSubjectSubscription!: Subscription;
    filters: any;

    constructor(
        private _mitiService: GrantsService,
        private dialog: MatDialog
    ) {}

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        if (
            this.sort &&
            this.sortMenu &&
            !this.sortMenu.nativeElement.contains(event.target)
        ) {
            this.sort = false;
        }
        if (
            this.showMainFilter &&
            this.filterMenu &&
            !this.filterMenu.nativeElement.contains(event.target)
        ) {
            this.showMainFilter = false;
        }
        if (
            this.showAppliedFilter &&
            this.appliedFiltersDiv &&
            !this.appliedFiltersDiv.nativeElement.contains(event.target)
        ) {
            this.showAppliedFilter = false;
        }
    }
    // date validator function for expired products
    isEndDateValid(appEnddt: string): boolean {
        if (!appEnddt) {
            return false;
        }

        const endDate = new Date(appEnddt);

        // Invalid date check::KSK
        if (isNaN(endDate.getTime())) {
            return false;
        }

        const now = new Date();

        // true → not expired, false → expired::KSK
        return endDate.getTime() >= now.getTime();
    }



    ngOnInit(): void {
        this._mitiService.getMitiData().subscribe((data) => {
            if (data?.length > 0) {
                const activeAgencies = data.filter(
                    (item: any) => item.prodActive === 'Y' && this.isEndDateValid(item.appEnddt)
                );
                this.mitiData = activeAgencies.sort((a, b) => {
                    if (a.prodFi.localeCompare(b.prodFi) !== 0) {
                        return a.prodFi.localeCompare(b.prodFi);
                    }
                    return a.productName.localeCompare(b.productName);
                });
                this.filteredMitiData = [...this.mitiData];
                this.imgs = Array.from(
                    new Set(this.mitiData.map((item: any) => item.image))
                );
                this._agencyFilters();
            }
        });
        this._openDialog();
    }

    _agencyFilters() {
        const activeFilters = new Set(
            this.mitiData.map((item: any) => item.prodFi)
        );
        const agencyFilter = filtersOptions.find(({ id }) => id === 'ag');
        if (!agencyFilter) return;
        const existingFilter = new Set(
            agencyFilter.subFilters.map((sub) => sub.filterBy)
        );
        activeFilters.forEach((filter) => {
            if (!existingFilter.has(filter)) {
                agencyFilter.subFilters.push({
                    label: filter,
                    labelBM: filter,
                    filterBy: filter,
                });
                existingFilter.add(filter);
            }
        });
        this.filters = [...filtersOptions];
    }

    _openDialog() {
        this.mitiSubjectSubscription = this._mitiService.mitiSubject.subscribe(
            (res) => {
                if (res) {
                    const isMobile = window.innerWidth <= 768;
                    const dialogRef = this.dialog.open(MitiDialogComponent, {
                        width: isMobile ? '90vw' : '40%',
                        maxWidth: isMobile ? '90vw' : 'none',
                        data: { message: 'This is a sample dialog' },
                    });

                    dialogRef.afterClosed().subscribe((result) => {
                        this._mitiService.isClickedOnStart(false);
                        if (result) {
                            this.isFormFilter = true;
                            this.formResults = result;
                            this.applyYearFilter(result);
                        }
                    });
                }
            }
        );
    }

    ngOnDestroy(): void {
        if (this.mitiSubjectSubscription) {
            this.mitiSubjectSubscription.unsubscribe();
        }
    }

    applyYearFilter(el: any) {
        this.selectedFilters = {};
        this.appliedFilters = [];
        this.isFiltersApplied = false;
        if (el) {
            this.filteredMitiData = this.mitiData.filter((item) => {
                const matchesYear =
                    item.yearsInOperation === el.operationDuration;
                const matchesFundingAmount =
                    el.fundingAmount &&
                    item.fundvalue.includes(el.fundingAmount);
                const matchesIndustry =
                    el.industry &&
                    el.industry.some((industryId) =>
                        item.sector.includes(industryId)
                    );
                return matchesYear && matchesFundingAmount && matchesIndustry;
            });
        } else {
            this.filteredMitiData = [...this.mitiData];
        }
    }

    onSearch(): void {
        this.activeSort = null;
        this.selectedFilters = {};
        this.appliedFilters = [];
        this.isFiltersApplied = false;

        const searchTextLower = this.searchText.toLowerCase();
        const isBM = this.selectedLanguage === 'bm';

        this.filteredMitiData = this.mitiData.filter(
            (item) =>
                (item.prodFi &&
                    item.prodFi.toLowerCase().includes(searchTextLower)) ||
                (item.productName &&
                    item.productName.toLowerCase().includes(searchTextLower)) ||
                (isBM
                    ? item.prodShortDescBm &&
                      item.prodShortDescBm
                          .toLowerCase()
                          .includes(searchTextLower)
                    : item.prodShortDesc &&
                      item.prodShortDesc
                          .toLowerCase()
                          .includes(searchTextLower))
        );
        this.totalMatchedCount = this.filteredMitiData.length;
        this.isFormFilter = false;
    }

    sortMitiData(criteria: string, data: any[] = this.filteredMitiData) {
        if (this.activeSort === criteria) {
            this.activeSort = null;
        } else {
            this.activeSort = criteria;
        }

        if (criteria == 'date-old' || criteria == 'date-new') {
            const sortFunctions = {
                'date-old': (a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    if (dateA !== dateB) {
                        return dateA - dateB;
                    }
                    return a.prodFi.localeCompare(b.prodFi);
                },
                'date-new': (a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    if (dateA !== dateB) {
                        return dateB - dateA;
                    }
                    return b.prodFi.localeCompare(a.prodFi);
                },
            };

            const sortFunction = sortFunctions[criteria];
            if (sortFunction) {
                data.sort(sortFunction);
                this.filteredMitiData = data;
            }
        } else if (criteria == 'fund-low-high' || criteria == 'fund-high-low') {
            if (criteria === 'fund-low-high') {
                this.filteredMitiData = this.sortByFundingAmount(data, 'asc');
            } else {
                this.filteredMitiData = this.sortByFundingAmount(data, 'desc');
            }
        }
        this.sort = false;
        this.isFormFilter = false;
    }

    sortByFundingAmount(data, order = 'asc') {
        const fundingFilter = filtersOptions.find(
            (category) => category.id === 'fa'
        );
        if (!fundingFilter) {
            throw new Error('Funding Amount filter (id: "fa") not found.');
        }

        const labelMap = fundingFilter.subFilters.reduce((acc, subFilter) => {
            let numericValue = Infinity;
            const match = subFilter.label.match(/\d{1,3}(?:,\d{3})*/);

            if (match) {
                numericValue = parseInt(match[0].replace(/,/g, ''), 10);
            }

            acc[subFilter.filterBy] = numericValue;
            return acc;
        }, {});

        return data
            .map((item) => {
                const fundValues = item.fundvalue.replace(/\|/g, '').split(',');

                const numericValue = labelMap[fundValues[0]] ?? Infinity;

                return { ...item, sortValue: numericValue };
            })
            .sort((a, b) =>
                order === 'asc'
                    ? a.sortValue - b.sortValue
                    : b.sortValue - a.sortValue
            )
            .map(({ sortValue, ...rest }) => rest);
    }

    toggleFilter(): void {
        this.showAppliedFilter = !this.showAppliedFilter;
        event.stopPropagation();
        this.showMainFilter = !this.showMainFilter;
    }

    toggleSubFilter(index: number): void {
        this.showSubFilters[index] = !this.showSubFilters[index];
    }

    toggleSort() {
        if (this.searchText !== '') {
            this.searchText = '';
            this.filteredMitiData = [...this.mitiData];
        }
        event.stopPropagation();
        this.sort = !this.sort;
    }

    toggleShowAll(event: Event): void {
        event.stopPropagation();
        this.showAll = !this.showAll;
    }

    onClose() {
        this.sort = false;
    }

    onFilter(filterName: string, subFilter: any) {
        this.activeSort = null;
        this.searchText = '';

        if (!this.selectedFilters[filterName]) {
            this.selectedFilters[filterName] = [];
        }

        const selectedValues = this.selectedFilters[filterName];
        const isAllSelected = subFilter.filterBy === 'All';
        const index = selectedValues.indexOf(subFilter.filterBy);

        const filter = filtersOptions.find((f) => f.name === filterName);
        const allOptions = filter?.subFilters
            .map((sub) => sub.filterBy)
            .filter((filterBy) => filterBy !== 'All');

        if (isAllSelected) {
            // If "All" is already selected, clear everything
            if (selectedValues.includes('All')) {
                this.selectedFilters[filterName] = [];
            } else {
                // Otherwise, select all except "All"
                if (allOptions) {
                    this.selectedFilters[filterName] = [...allOptions, 'All'];
                }
            }
        } else {
            // If "All" was previously selected, we need to unselect it immediately
            if (selectedValues.includes('All')) {
                selectedValues.splice(selectedValues.indexOf('All'), 1);
            }

            // If the option is not already selected, add it
            if (index === -1) {
                selectedValues.push(subFilter.filterBy);
            } else {
                // If it is selected, remove it
                selectedValues.splice(index, 1);
            }

            // If all options except "All" are selected, add "All"
            if (
                allOptions &&
                selectedValues.length === allOptions.length &&
                !selectedValues.includes('All')
            ) {
                selectedValues.push('All');
            }
        }

        this.updateAppliedFilters();
        this.applyFilters();
        this.isFormFilter = false;
    }

    updateAppliedFilters() {
        this.appliedFilters = [];
        for (const filterName in this.selectedFilters) {
            this.selectedFilters[filterName].forEach((value) => {
                const filter = filtersOptions.find(
                    (f) => f.name === filterName
                );
                const label =
                    filter?.subFilters.find((sub) => sub.filterBy === value)
                        ?.label || value;
                this.appliedFilters.push({ name: filterName, value: label });
            });
        }
        this.isFormFilter = false;
    }

    removeFilter(index: number) {
        event.stopPropagation();
        const filterToRemove = this.appliedFilters[index];
        if (filterToRemove) {
            const filterName = filterToRemove.name;
            const filterLabel = filterToRemove.value;
            const filter = filtersOptions.find((f) => f.name === filterName);
            const filterByValue = filter?.subFilters.find(
                (sub) => sub.label === filterLabel
            )?.filterBy;

            if (filterByValue) {
                this.selectedFilters[filterName] = this.selectedFilters[
                    filterName
                ].filter((val) => val !== filterByValue);
                if (this.selectedFilters[filterName].length === 0) {
                    delete this.selectedFilters[filterName];
                }
                this.updateAppliedFilters();
                this.applyFilters();
            }
        }
    }

    clearAllFilters() {
        this.selectedFilters = {};
        this.appliedFilters = [];
        this.isFormFilter = false;
        this.applyFilters();
        event.stopPropagation();
    }

    applyFilters() {
        this.isFiltersApplied = true;
        let filtered = [...this.mitiData];

        for (let filterKey in this.selectedFilters) {
            const selectedValues = this.selectedFilters[filterKey].filter(
                (val) => val !== 'All'
            );

            if (selectedValues.length > 0) {
                filtered = filtered.filter((product) => {
                    switch (filterKey) {
                        case 'GRANTS.AGENCY':
                            return selectedValues.some(
                                (selected) => selected === product.prodFi
                            );
                        case 'GRANTS.BUSINESS_OPERATION_YEARS':
                            return selectedValues.some(
                                (selected) =>
                                    selected === product.yearsInOperation
                            );
                        case 'GRANTS.INDUSTRY':
                            return selectedValues.some((selected) =>
                                product.sector.includes(`|${selected}|`)
                            );
                        case 'GRANTS.FUNDING_AMOUNT':
                            return selectedValues.some((selected) =>
                                product.fundvalue.includes(`|${selected}|`)
                            );
                        default:
                            return true;
                    }
                });
            }
        }

        if (this.activeSort) {
            this.sortMitiData(this.activeSort, filtered);
        } else {
            this.filteredMitiData = filtered;
        }

        this.totalMatchedCount = this.filteredMitiData.length;

        // If no filters are selected, reset the count to zero
        if (
            Object.keys(this.selectedFilters).length === 0 ||
            Object.values(this.selectedFilters).every((val) => val.length === 0)
        ) {
            this.totalMatchedCount = 0;
        }

        this.isFormFilter = false;
    }

    getFilterLabel = (filterBy, language, id) => {
        const languages = ['en', 'bm'];
        if (!languages.includes(language)) {
            throw new Error(
                'Invalid language. Please use "en" for English or "bm" for Bahasa Malaysia.'
            );
        }

        const filterCategory = filtersOptions.find(
            (category) => category.id === id
        );
        if (!filterCategory) {
            throw new Error('Invalid filter category ID.');
        }

        const subFilter = filterCategory.subFilters.find(
            (sub) => sub.filterBy === filterBy
        );
        return subFilter
            ? language === 'en'
                ? subFilter.label
                : subFilter.labelBM
            : null;
    };
}
